import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, Mail, MailX, Bell, BellOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminUsuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role_id: "", notify_email: true, notify_push: false, sucursales: [], tabs: [], can_manage_inventory: false });
  const [saving, setSaving] = useState(false);


  const load = async () => {
    try {
      const [u, r, s] = await Promise.all([api.get("/users"), api.get("/roles"), api.get("/sucursales")]);
      setUsers(u.data);
      setRoles(r.data);
      setSucursales(s.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ALL_TABS = [
    { key: "piso", label: "Piso" },
    { key: "produccion", label: "Producción" },
    { key: "helados", label: "Helados" },
    { key: "paletas", label: "Paletas" },
  ];

  const toggleTab = (tabKey) => {
    setForm((f) => {
      const has = f.tabs.includes(tabKey);
      return { ...f, tabs: has ? f.tabs.filter((t) => t !== tabKey) : [...f.tabs, tabKey] };
    });
  };

  const toggleSucursal = (sucId) => {
    setForm((f) => {
      const has = f.sucursales.includes(sucId);
      return { ...f, sucursales: has ? f.sucursales.filter((s) => s !== sucId) : [...f.sucursales, sucId] };
    });
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      username: "",
      email: "",
      password: "",
      role_id: roles[0]?.id || "",
      notify_email: true,
      notify_push: false,
      sucursales: me?.sucursales || (sucursales[0] ? [sucursales[0].id] : []),
      tabs: ["piso", "helados", "paletas"],
      can_manage_inventory: false,
    });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      username: u.username || "",
      email: u.email || "",
      password: "",
      role_id: u.role_id,
      notify_email: u.notify_email ?? true,
      notify_push: u.notify_push ?? false,
      sucursales: u.sucursales || [],
      tabs: u.tabs || [],
      can_manage_inventory: !!u.can_manage_inventory,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.username.trim() || !form.role_id) {
      return toast.error("Nombre, usuario y rol son obligatorios");
    }
    if (!editing && !form.password) return toast.error("La contraseña es obligatoria");
    setSaving(true);
    try {
      // Build payload: omit empty optional fields
      const payload = { ...form };
      payload.username = payload.username.trim().toLowerCase();
      if (!payload.email) {
        if (editing) payload.email = null;
        else delete payload.email;
      }
      // sucursales: always send an array; empty means "todas las sucursales" (super-admin)
      payload.sucursales = Array.isArray(payload.sucursales) ? payload.sucursales : [];
      if (editing && !payload.password) delete payload.password;
      if (editing) {
        const { data } = await api.patch(`/users/${editing.id}`, payload);
        setUsers((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Usuario actualizado");
      } else {
        const { data } = await api.post("/users", payload);
        setUsers((p) => [...p, data]);
        toast.success("Usuario creado");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success("Usuario eliminado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#4A3B32]" data-testid="usuarios-title">Usuarios</h1>
          <p className="text-[#8A796F]">Gestiona quién accede y con qué rol.</p>
        </div>
        <Button onClick={openNew} data-testid="user-create-button" className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold px-6">
          <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </header>

      {/* Mobile: card layout */}
      <div className="md:hidden grid grid-cols-1 gap-3">
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`user-row-${u.id}`}
            className="bg-white border border-[#EADBCE] rounded-2xl p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-display font-bold text-[#4A3B32] truncate">{u.name}</h3>
                <p className="text-sm text-[#4A3B32] font-mono truncate">@{u.username || "—"}</p>
                {u.email ? (
                  <p className="text-xs text-[#8A796F] truncate mt-0.5">{u.email}</p>
                ) : (
                  <p className="text-xs text-[#EADBCE] mt-0.5">Sin email</p>
                )}
                <p className="text-[10px] text-[#8A796F] mt-1">
                  {(u.sucursales_list && u.sucursales_list.length > 0)
                    ? u.sucursales_list.map((s) => s.name).join(", ")
                    : "Todas las sucursales"}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-1.5 flex-wrap justify-end">
                {u.can_manage_inventory && !u.role?.is_manager && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DCF7DF] text-[#1E7E34] font-bold border border-[#9EF07A]" title="Puede crear items, sabores y paletas">
                    Editor
                  </span>
                )}
                <span className="text-xs px-2.5 py-1 rounded-full bg-[#F7C8E0]/40 font-semibold text-[#4A3B32]">
                  {u.role?.name || "—"}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EADBCE]">
              <span className="text-xs text-[#8A796F] flex items-center gap-2">
                {u.notify_email && u.email ? (
                  <Mail className="w-3.5 h-3.5 text-[#A7E4D3]" title="Email activo" />
                ) : (
                  <MailX className="w-3.5 h-3.5 text-[#EADBCE]" title="Sin email" />
                )}
                {u.notify_push ? (
                  <Bell className="w-3.5 h-3.5 text-[#FF7878]" title="Push activo" />
                ) : (
                  <BellOff className="w-3.5 h-3.5 text-[#EADBCE]" title="Push apagado" />
                )}
                <span className="ml-1">Notificaciones</span>
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} className="rounded-full">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                </Button>
                {u.id !== me.id && (
                  <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="rounded-full text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FFF0F0]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table layout */}
      <div className="hidden md:block bg-white border border-[#EADBCE] rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#FCF9F2] text-xs uppercase tracking-wide text-[#8A796F]">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Usuario</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Rol</th>
              <th className="text-center px-4 py-3">Notif.</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#EADBCE]" data-testid={`user-row-desktop-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-[#4A3B32]">{u.name}</td>
                <td className="px-4 py-3 text-[#4A3B32] font-mono text-sm">@{u.username || "—"}</td>
                <td className="px-4 py-3 text-[#8A796F]">{u.email || <span className="text-[#EADBCE]">—</span>}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#F7C8E0]/40 font-semibold text-[#4A3B32]">
                    {u.role?.name || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    {u.notify_email && u.email ? <Mail className="w-4 h-4 text-[#A7E4D3]" /> : <MailX className="w-4 h-4 text-[#EADBCE]" />}
                    {u.notify_push ? <Bell className="w-4 h-4 text-[#FF7878]" /> : <BellOff className="w-4 h-4 text-[#EADBCE]" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-desktop-${u.id}`} className="rounded-full">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {u.id !== me.id && (
                    <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`user-delete-desktop-${u.id}`} className="rounded-full text-[#FF5C5C]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Juan Pérez"
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Usuario <span className="text-xs text-[#8A796F]">(usado para iniciar sesión)</span>
              </Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
                placeholder="juan_perez"
                autoCapitalize="none"
                spellCheck={false}
                className="h-12 rounded-xl border-[#EADBCE] font-mono"
                data-testid="user-username-input"
              />
              <p className="text-xs text-[#8A796F]">3–30 caracteres. Solo letras minúsculas, números, punto, guion o guion bajo.</p>
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-xs text-[#8A796F]">(opcional, para recibir notificaciones)</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="opcional@correo.com"
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña {editing && <span className="text-xs text-[#8A796F]">(opcional)</span>}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Dejar vacío para mantener" : ""}
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                <SelectTrigger className="h-12 rounded-xl border-[#EADBCE]" data-testid="user-role-select">
                  <SelectValue placeholder="Selecciona un rol" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`user-role-option-${r.name}`}>
                      {r.name} {r.is_manager && " (Gerente)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Sucursales asignadas
                <span className="text-xs text-[#8A796F] ml-1">(vacío = ve todas las sucursales)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {sucursales.map((s) => {
                  const active = form.sucursales.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSucursal(s.id)}
                      data-testid={`user-sucursal-pill-${s.name}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                        active
                          ? "bg-[#FF7878] border-[#FF7878] text-white"
                          : "bg-white border-[#EADBCE] text-[#4A3B32] hover:bg-[#FCF9F2]"
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
              {form.sucursales.length === 0 && (
                <p className="text-xs text-[#8A796F]">
                  Sin selección: verá <b>todas</b> las sucursales (super-Gerente).
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                Pestañas
                <span className="text-xs text-[#8A796F] ml-1">(solo aplica a Empleado; Gerente ve todo)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {ALL_TABS.map((t) => {
                  const active = form.tabs.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => toggleTab(t.key)}
                      data-testid={`user-tab-pill-${t.key}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                        active
                          ? "bg-[#FF7878] border-[#FF7878] text-white"
                          : "bg-white border-[#EADBCE] text-[#4A3B32] hover:bg-[#FCF9F2]"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border-2 border-[#9EF07A] bg-[#F5FFF7] p-3">
              <div className="min-w-0 pr-2">
                <p className="font-semibold text-sm text-[#4A3B32]">Editor de inventario</p>
                <p className="text-xs text-[#8A796F]">
                  Puede crear, editar y borrar items, sabores y paletas (sólo en sus sucursales). No puede tocar usuarios ni configuración.
                </p>
              </div>
              <Switch
                checked={form.can_manage_inventory}
                onCheckedChange={(v) => setForm({ ...form, can_manage_inventory: v })}
                data-testid="user-can-manage-inventory-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#EADBCE] p-3">
              <div>
                <p className="font-semibold text-sm text-[#4A3B32]">Recibir emails de reorden</p>
                <p className="text-xs text-[#8A796F]">Solo aplica si el rol es Gerente y tiene email.</p>
              </div>
              <Switch
                checked={form.notify_email}
                onCheckedChange={(v) => setForm({ ...form, notify_email: v })}
                data-testid="user-notify-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#EADBCE] p-3">
              <div>
                <p className="font-semibold text-sm text-[#4A3B32]">Recibir notificaciones push</p>
                <p className="text-xs text-[#8A796F]">Solo aplica al rol Gerente. Cada dispositivo debe activarse desde Config.</p>
              </div>
              <Switch
                checked={form.notify_push}
                onCheckedChange={(v) => setForm({ ...form, notify_push: v })}
                data-testid="user-notify-push-switch"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
              data-testid="user-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
